import { FC, useEffect, useState } from 'react';
import NumberInput from '../custom-input/NumberInput';
import Loader from '../loader/Loader';
import { SelectedSpecimenQuantity } from '../specimen-tables/SelectSpecimens';
import { SpecimenQuantity } from '../../types/interfaces/bulkOrder.interfaces';
import { Specimen } from '../../types/interfaces';
import { getSpecimenLocations } from '../../api/specimensApi';
import { showToast } from '../../services/toast.service';
import { dateFormatMMDDYYYY } from '../../utils/commonUtils';
import { toastMessages } from '../../constants/errorMessages';
import { LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import './multiItemStorageLocations.scss';

export interface SpecimenQuantities extends SpecimenQuantity {
  specimen: Specimen;
}

type MultiItemStorageLocationsProps = {
  selectedSpecimens: SelectedSpecimenQuantity[];
  onLocationsChange: (groupedLocations: { specimenId: number; specimenLocations: SpecimenQuantities[] }[]) => void;
};

const MultiItemStorageLocations: FC<MultiItemStorageLocationsProps> = ({ selectedSpecimens, onLocationsChange }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [groupedLocations, setGroupedLocations] = useState<{ specimenId: number; specimenLocations: SpecimenQuantities[] }[]>([]);

  const locationHeaders: string[] = [
    TABLE_HEADER_CONSTANTS.SELECT,
    TABLE_HEADER_CONSTANTS.LOCATION,
    TABLE_HEADER_CONSTANTS.ANIMAL,
    TABLE_HEADER_CONSTANTS.LOT_DATE_NO,
    TABLE_HEADER_CONSTANTS.QTY_IN_CANISTER,
    TABLE_HEADER_CONSTANTS.WITHDRAW_QTY,
  ];

  const totalSelectedQuantity: number = groupedLocations.reduce(
    (acc, specimen) =>
      acc +
      (specimen.specimenLocations
        ? specimen.specimenLocations.filter(loc => loc.selected).reduce((locAcc, loc) => locAcc + loc.quantity, 0)
        : 0),
    0,
  );

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const specimenIds = selectedSpecimens.map(specimen => specimen.specimenId);
        const newSpecimenIds = specimenIds.filter(id => !groupedLocations.some(location => location.specimenId === id));
        const specimenIdsToRemove = groupedLocations
          .filter(location => !specimenIds.includes(location.specimenId))
          .map(location => location.specimenId);

        const fetchPromises = newSpecimenIds.map(specimenId => {
          const query = {
            include: 'Specimen.Animal, Specimen.SpecimenLocations.StorageCanister.StorageTank.StorageSite',
            filter: `specimenId eq ${specimenId}`,
          };

          return getSpecimenLocations(query).then(response => {
            const specimenLocations = response.data.map((specimenLocation: any) => ({
              specimenId: specimenLocation.specimenId!,
              availableQuantity: specimenLocation.quantity,
              quantity: 0,
              selected: false,
              freezeDate: specimenLocation?.specimen?.freezeDate || '',
              selectedCanisterId: specimenLocation.storageCanisterId!,
              specimenLocationId: specimenLocation.specimenLocationId!,
              storageCanister: specimenLocation.storageCanister!,
              transactionLinkId: specimenLocation.transactionLinkId,
              specimen: specimenLocation.specimen,
            }));

            return { specimenId, specimenLocations };
          });
        });

        const updatedGroupedLocations = groupedLocations.filter(location => !specimenIdsToRemove.includes(location.specimenId));
        const newLocations = await Promise.all(fetchPromises);

        if (!isCancelled) {
          setGroupedLocations([...updatedGroupedLocations, ...newLocations]);
        }
      } catch (error) {
        if (!isCancelled) showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    if (selectedSpecimens.length > 0) {
      fetchData();
    } else {
      setGroupedLocations([]);
      setLoading(false);
    }

    return () => {
      isCancelled = true;
    };
  }, [selectedSpecimens]);

  useEffect(() => {
    onLocationsChange(groupedLocations);
  }, [groupedLocations]);

  const renderStorageLocationTableRows = (location: SpecimenQuantities, index: number) => {
    const isTravelingTank = location.storageCanister?.storageTank?.isTravelingTank;
    const rowClass = `${handleErrors(location)} ${isTravelingTank ? 'disabled' : ''}`;
    const locationPath = `${location.storageCanister?.storageTank?.storageSite?.name}/${location.storageCanister?.storageTank?.name}/${location.storageCanister?.name}`;
    const animalDetails = `${location.specimen.animal?.code}-${location.specimen.animal?.name}`;
    const lotDetails = `${dateFormatMMDDYYYY(location.freezeDate)} - ${location.specimenId}`;

    return (
      <tr className={rowClass} key={`location-row-${location.specimenId}-${index}`}>
        <td>
          <input
            type="checkbox"
            id={`location-selected-${index}`}
            checked={location.selected ?? false}
            onChange={e => handleLocationSelected(e, location.specimenId, index)}
            disabled={isTravelingTank}
          />
        </td>
        <td>{locationPath}</td>
        <td>{animalDetails}</td>
        <td>{lotDetails}</td>
        <td>{location.availableQuantity}</td>
        <td>
          {isTravelingTank ? (
            LABEL_CONSTANTS.TRAVELING_TANK
          ) : (
            <input
              type="text"
              placeholder="0"
              value={location.selected ? (location.quantity > 0 ? location.quantity : '') : '-'}
              onChange={e => handleQuantities(e, location.specimenId, index)}
              disabled={!location.selected}
            />
          )}
        </td>
      </tr>
    );
  };

  const renderStorageLocationMobileRow = (location: SpecimenQuantities, index: number) => {
    const isTravelingTank = location.storageCanister?.storageTank?.isTravelingTank;
    const rowClass = `card max-width ${handleErrors(location)} ${isTravelingTank ? 'disabled' : ''}`;
    const locationPath = `${location.storageCanister?.storageTank?.storageSite?.name} / ${location.storageCanister?.storageTank?.name} / ${location.storageCanister?.name}`;
    const animalDetails = `${location.specimen.animal?.code}-${location.specimen.animal?.name}`;
    const lotDetails = `${dateFormatMMDDYYYY(location.freezeDate)} - ${location.specimenId}`;

    return (
      <div key={`location-row-${location.specimenId}-${index}`} className={rowClass}>
        <div className="item-storage-locations-row">
          <div className="input-check">
            <input
              type="checkbox"
              id={`location-selected-${index}`}
              checked={location.selected ?? false}
              onChange={e => handleLocationSelected(e, location.specimenId, index)}
              disabled={isTravelingTank}
            />
          </div>
          <div className="item-storage-locations-content">
            <label>{locationPath}</label>
            <label>{animalDetails}</label>
            <label>{lotDetails}</label>
            <label>{`${TABLE_HEADER_CONSTANTS.QTY_IN_CANISTER} : ${location.availableQuantity}`}</label>
          </div>
          <div className="number-input-section">
            {isTravelingTank ? (
              <label>{LABEL_CONSTANTS.TRAVELING_TANK}</label>
            ) : (
              <>
                <label>{TABLE_HEADER_CONSTANTS.WITHDRAW_QTY}</label>
                <NumberInput
                  onChange={e => handleQuantities(e, location.specimenId, index)}
                  value={location.quantity ?? 0}
                  disabled={!location.selected}
                />
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleErrors = (location: SpecimenQuantities) => {
    return location.selected
      ? location.quantity > location.availableQuantity
        ? 'error'
        : location.quantity === 0
        ? 'warning'
        : ''
      : '';
  };

  const handleLocationSelected = (changeEvent: React.ChangeEvent<HTMLInputElement>, id: number, index: number) => {
    let newTransactions = [...groupedLocations];

    let updatedTransactions = newTransactions.map(transaction => {
      if (transaction.specimenId === id && transaction.specimenLocations) {
        const updatedLocations = [...transaction.specimenLocations];
        updatedLocations[index] = {
          ...transaction.specimenLocations[index],
          selected: changeEvent.target.checked,
          quantity: changeEvent.target.checked ? transaction.specimenLocations[index].quantity : 0,
        };

        return { ...transaction, specimenLocations: updatedLocations };
      } else {
        return transaction;
      }
    });

    setGroupedLocations(updatedTransactions);
  };

  const handleQuantities = (changeEvent: React.ChangeEvent<HTMLInputElement>, id: number, index: number) => {
    let newTransactions = [...groupedLocations];

    let updatedQuantities = newTransactions.map(transaction => {
      if (transaction.specimenId === id && transaction.specimenLocations) {
        const updatedLocationQuantities = [...transaction.specimenLocations];
        updatedLocationQuantities[index] = {
          ...transaction.specimenLocations[index],
          quantity: Number(changeEvent.target.value),
        };

        return { ...transaction, specimenLocations: updatedLocationQuantities };
      } else {
        return transaction;
      }
    });

    setGroupedLocations(updatedQuantities);
  };

  return (
    <div className="item-storage-locations">
      <>
        <h3>{LABEL_CONSTANTS.ITEM_STORAGE_LOCATIONS}</h3>
        <div className="desk-item-storage-locations">
          <div className="specimen-table">
            <table>
              <thead>
                <tr>
                  {locationHeaders.map((header, index) => (
                    <th key={`${header}-${index}`}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupedLocations?.map(location => {
                  return location?.specimenLocations?.map((location, index) => renderStorageLocationTableRows(location, index));
                })}
                {loading && (
                  <tr>
                    <td colSpan={6}>
                      <>
                        <br />
                        <br />
                        <Loader loaderSize="small" />
                        <br />
                        <br />
                      </>
                    </td>
                  </tr>
                )}
                {selectedSpecimens.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <>
                        <p>{LABEL_CONSTANTS.NO_ITEM_SELECTED}</p>
                      </>
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={5}></td>
                  <td colSpan={1}>
                    <div className="total-selected-quantity">
                      <input type="text" disabled value={totalSelectedQuantity} />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/** Mobile View */}
        <div className="xs-item-storage-locations">
          {groupedLocations?.map(location => {
            return location.specimenLocations?.map((location, index) => renderStorageLocationMobileRow(location, index));
          })}

          <div className="total-quantity">
            <label>{LABEL_CONSTANTS.TOTAL_QUANTITY_SELECTED}: &nbsp; </label>
            {totalSelectedQuantity}
          </div>
        </div>
      </>
    </div>
  );
};

export default MultiItemStorageLocations;
